import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHeadline from '../components/PageHeadline';
import BankAccountChoices from '../components/BankAccountChoices';
import supabase from '../supabase/supabase';
import { bankDetails } from '../data/catalog';

const { FiX, FiUpload, FiCheck, FiCreditCard, FiArrowLeft, FiTrash2, FiCopy, FiTag } = FiIcons;

const SRI_LANKA_PROVINCES = [
  'Central',
  'Eastern',
  'North Central',
  'Northern',
  'North Western',
  'Sabaragamuwa',
  'Southern',
  'Uva',
  'Western'
];

const CHECKOUT_DETAILS_STORAGE_KEY = 'darshana-checkout-details';
const DEFAULT_CHECKOUT_FORM = {
  name: '',
  email: '',
  phone: '',
  address: '',
  province: 'Western'
};

function loadCheckoutDetails() {
  try {
    const savedDetails = window.localStorage.getItem(CHECKOUT_DETAILS_STORAGE_KEY);
    const parsedDetails = savedDetails ? JSON.parse(savedDetails) : {};

    if (!parsedDetails || typeof parsedDetails !== 'object' || Array.isArray(parsedDetails)) {
      return DEFAULT_CHECKOUT_FORM;
    }

    return {
      name: typeof parsedDetails.name === 'string' ? parsedDetails.name : '',
      email: typeof parsedDetails.email === 'string' ? parsedDetails.email : '',
      phone: typeof parsedDetails.phone === 'string' ? parsedDetails.phone : '',
      address: typeof parsedDetails.address === 'string' ? parsedDetails.address : '',
      province: SRI_LANKA_PROVINCES.includes(parsedDetails.province)
        ? parsedDetails.province
        : DEFAULT_CHECKOUT_FORM.province
    };
  } catch {
    return DEFAULT_CHECKOUT_FORM;
  }
}

function generateTrackingNumber() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = 'DG-';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function generateTrackingToken() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

function formatPhoneWithPrefix(phone) {
  const cleaned = phone.replace(/[\s\-()]/g, '');
  if (cleaned.startsWith('+94')) return cleaned;
  if (cleaned.startsWith('94') && cleaned.length >= 11) return '+' + cleaned;
  if (cleaned.startsWith('0')) return '+94' + cleaned.slice(1);
  return '+94' + cleaned;
}

function CheckoutPage({ cart, setCart }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(loadCheckoutDetails);
  const [shippingRates, setShippingRates] = useState([]);
  const [bankInfo, setBankInfo] = useState(bankDetails);
  const [additionalBanks, setAdditionalBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('default');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      window.localStorage.setItem(CHECKOUT_DETAILS_STORAGE_KEY, JSON.stringify(form));
    } catch (error) {
      console.error('Could not save checkout details:', error);
    }
  }, [form]);

  useEffect(() => {
    supabase.from('shipping_rates_1750000002').select('*').eq('active', true).then(({ data }) => {
      setShippingRates(data || []);
    });
    supabase.from('settings_1750000003').select('*').eq('key', 'bank_details').limit(1).single()
      .then(({ data }) => {
        if (data && data.value) setBankInfo(data.value);
      });
    supabase.from('bank_transfer_accounts_1750000019').select('*').eq('active', true)
      .order('sort_order').order('created_at')
      .then(({ data }) => setAdditionalBanks(data || []));
  }, []);

  const bankAccounts = additionalBanks;
  const selectedBank = bankAccounts.find((account) => account.id === selectedBankId) || bankAccounts[0];
  const subtotal = cart.reduce((sum, item) => sum + item.price, 0);
  const shippingRate = shippingRates.find((r) => (r.province || r.district) === form.province);
  const shippingCost = shippingRate ? Number(shippingRate.rate) : 0;
  const discountAmount = appliedPromo ? Math.round(subtotal * appliedPromo.discount_percent / 100) : 0;
  const total = subtotal + shippingCost - discountAmount;

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const removeItem = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const handleScreenshot = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshot(file);
      const reader = new FileReader();
      reader.onload = (ev) => setScreenshotPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const copyTracking = () => {
    if (orderResult?.tracking) {
      navigator.clipboard.writeText(orderResult.tracking);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const applyPromoCode = async () => {
    const code = promoInput.trim();
    if (!code) {
      setPromoError('Please enter a promo code');
      return;
    }
    setPromoLoading(true);
    setPromoError('');

    try {
      const { data, error } = await supabase.rpc('validate_any_promo_code_1750000012', { code_input: code });
      if (error) throw error;

      if (!data || data.length === 0) {
        setPromoError('Invalid or expired promo code');
        setAppliedPromo(null);
      } else {
        const match = data[0];
        setAppliedPromo({
          source: match.source,
          code: match.promo_code,
          discount_percent: Number(match.discount_percent),
          influencer_id: match.influencer_id
        });
        setPromoError('');
      }
    } catch (err) {
      setPromoError('Could not validate promo code');
    }
    setPromoLoading(false);
  };

  const removePromo = () => {
    setPromoInput('');
    setAppliedPromo(null);
    setPromoError('');
  };

  const placeOrder = async () => {
    if (!screenshot) {
      alert('Please upload your payment receipt before completing your order.');
      return;
    }
    if (!selectedBank) {
      alert('No bank account is available for payment. Please try again later.');
      return;
    }

    setLoading(true);
    try {
      const trackingNumber = generateTrackingNumber();
      const trackingToken = generateTrackingToken();
      const formattedPhone = formatPhoneWithPrefix(form.phone);

      const orderData = {
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: formattedPhone,
        shipping_address: form.address,
        shipping_district: form.province,
        shipping_cost: shippingCost,
        total_amount: total,
        payment_method: 'bank_transfer',
        tracking_number: trackingNumber,
        tracking_token: trackingToken,
        status: 'pending'
      };

      if (appliedPromo) {
        orderData.promo_code = appliedPromo.code;
        orderData.promo_discount = appliedPromo.discount_percent;
        orderData.discount_amount = discountAmount;
      }

      const { data: order, error } = await supabase.from('orders_1750000001').insert(orderData).select().single();

      if (error) throw error;

      const items = cart.map((item) => ({
        order_id: order.id,
        product_id: item.id,
        product_name: item.name,
        quantity: 1,
        unit_price: item.price
      }));
      await supabase.from('order_items_1750000001').insert(items);

      await supabase.from('order_payments_1750000002').insert({
        order_id: order.id,
        payment_method: 'bank_transfer',
        bank_reference: `Transferred to ${selectedBank.bank_name} ••••${selectedBank.account_number.slice(-4)}`,
        screenshot_url: screenshotPreview || '',
        status: screenshot ? 'submitted' : 'pending'
      });

      if (appliedPromo && appliedPromo.source === 'influencer' && appliedPromo.influencer_id) {
        await supabase.rpc('record_influencer_sale_1750000011', {
          order_input: order.id,
          code_input: appliedPromo.code
        });
      }

      setOrderResult({ id: order.id, tracking: trackingNumber, token: trackingToken });
      setOrderComplete(true);
      setCart([]);
    } catch (err) {
      console.error('Order error:', err);
      alert('Failed to place order. Please try again.');
    }
    setLoading(false);
  };

  if (orderComplete) {
    return (
      <main className="checkout-page">
        <div className="checkout-complete section-shell">
          <div className="complete-icon"><SafeIcon icon={FiCheck} /></div>
          <PageHeadline>Order Placed Successfully!</PageHeadline>
          <p>Your order <strong>#{orderResult.id.slice(0, 8)}</strong> has been received.</p>

          <div className="tracking-number-display">
            <span className="tracking-label">Your Tracking Number</span>
            <div className="tracking-value-row">
              <span className="tracking-value">{orderResult.tracking}</span>
              <button className="copy-tracking-btn" onClick={copyTracking}>
                <SafeIcon icon={FiCopy} /> {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="tracking-hint">Save this number to track your order anytime</p>
          </div>

          <p>We'll verify your bank transfer and process your order shortly.</p>
          <div className="complete-actions">
            <Link to="/track" className="button primary gradient-btn">Track Order</Link>
            <Link to="/shop" className="button secondary">Continue Shopping</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <div className="checkout-header section-shell">
        <Link to="/shop"><SafeIcon icon={FiArrowLeft} /> Back to Shop</Link>
        <PageHeadline>Checkout</PageHeadline>
        <div className="checkout-steps">
          <span className={step >= 1 ? 'active' : ''}>1. Cart</span>
          <span className={step >= 2 ? 'active' : ''}>2. Details</span>
          <span className={step >= 3 ? 'active' : ''}>3. Payment</span>
        </div>
      </div>

      <div className="checkout-body section-shell">
        <div className="checkout-main">
          {step === 1 && (
            <div className="checkout-section">
              <h2>Your Cart ({cart.length} items)</h2>
              {cart.length === 0 ? (
                <div className="empty-cart">
                  <p>Your cart is empty</p>
                  <Link to="/shop" className="button primary">Browse Products</Link>
                </div>
              ) : (
                <>
                  <div className="cart-items">
                    {cart.map((item, i) => (
                      <div className="cart-item" key={`${item.id}-${i}`}>
                        <img src={item.image} alt={item.name} />
                        <div className="cart-item-info">
                          <h3>{item.name}</h3>
                          <span>{item.category}</span>
                          <strong>Rs. {item.price.toLocaleString()}</strong>
                        </div>
                        <button className="cart-remove" onClick={() => removeItem(i)}><SafeIcon icon={FiTrash2} /></button>
                      </div>
                    ))}
                  </div>
                  <button className="button primary gradient-btn" onClick={() => setStep(2)} style={{ marginTop: 20 }}>
                    Continue to Details
                  </button>
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="checkout-section">
              <h2>Shipping Details</h2>
              <div className="checkout-form">
                <div className="form-row">
                  <label>Full Name</label>
                  <input type="text" value={form.name} onChange={update('name')} required placeholder="Your full name" />
                </div>
                <div className="form-row">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={update('email')} required placeholder="your@email.com" />
                </div>
                <div className="form-row">
                  <label>Phone Number</label>
                  <div className="phone-input-wrap">
                    <span className="phone-prefix">+94</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update('phone')}
                      required
                      placeholder="7X XXX XXXX"
                    />
                  </div>
                  <small className="phone-hint">Sri Lankan mobile number (without leading 0)</small>
                </div>
                <div className="form-row">
                  <label>Shipping Address</label>
                  <textarea value={form.address} onChange={update('address')} required placeholder="Full shipping address" rows={3} />
                </div>
                <div className="form-row">
                  <label>Province</label>
                  <select value={form.province} onChange={update('province')}>
                    {SRI_LANKA_PROVINCES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  {shippingRate && (
                    <small className="phone-hint">Shipping: Rs. {Number(shippingRate.rate).toLocaleString()}</small>
                  )}
                  {!shippingRate && (
                    <small className="phone-hint" style={{ color: '#d97706' }}>No shipping rate set for this province</small>
                  )}
                </div>
              </div>
              <div className="checkout-nav-buttons">
                <button className="button secondary" onClick={() => setStep(1)}>Back</button>
                <button className="button primary gradient-btn" onClick={() => setStep(3)} disabled={!form.name || !form.email || !form.phone || !form.address}>
                  Continue to Payment
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="checkout-section">
              <h2>Bank Transfer Payment</h2>
              <div className="bank-details-card">
                <h3><SafeIcon icon={FiCreditCard} /> Transfer to this account</h3>
                <BankAccountChoices accounts={bankAccounts} selectedId={selectedBankId} onSelect={setSelectedBankId} amount={total} />
                {!selectedBank && <p>No bank account is available. Please contact us before paying.</p>}
              </div>

              <div className="screenshot-upload">
                <h3>Upload Payment Screenshot</h3>
                <p>After completing the transfer, upload your receipt below.</p>
                {screenshotPreview ? (
                  <div className="screenshot-preview">
                    <img src={screenshotPreview} alt="Payment receipt" />
                    <button onClick={() => { setScreenshot(null); setScreenshotPreview(''); }}>
                      <SafeIcon icon={FiX} /> Remove
                    </button>
                  </div>
                ) : (
                  <label className="upload-zone">
                    <SafeIcon icon={FiUpload} />
                    <span>Click to upload screenshot</span>
                    <input type="file" accept="image/*" onChange={handleScreenshot} hidden />
                  </label>
                )}
              </div>

              <div className="checkout-nav-buttons">
                <button className="button secondary" onClick={() => setStep(2)}>Back</button>
                <button className="button primary gradient-btn" onClick={placeOrder} disabled={loading || !screenshot || !selectedBank}>
                  {loading ? 'Processing…' : 'Complete Order'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="checkout-sidebar">
          <div className="order-summary">
            <h2>Order Summary</h2>
            <div className="summary-row"><span>Subtotal ({cart.length} items)</span><span>Rs. {subtotal.toLocaleString()}</span></div>
            <div className="summary-row"><span>Shipping ({form.province})</span><span>Rs. {shippingCost.toLocaleString()}</span></div>

            <div className="promo-code-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontWeight: 600, fontSize: 14 }}>
                <SafeIcon icon={FiTag} /> Promo Code
              </label>
              {appliedPromo ? (
                <div className="promo-applied">
                  <div className="promo-applied-info">
                    <span className="promo-badge">{appliedPromo.code}</span>
                    <span className="promo-discount">-{appliedPromo.discount_percent}%</span>
                  </div>
                  <button className="promo-remove-btn" onClick={removePromo} title="Remove promo code">
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
              ) : (
                <div className="promo-input-row">
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                    placeholder="Enter code"
                    onKeyDown={(e) => e.key === 'Enter' && applyPromoCode()}
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                  />
                  <button className="promo-apply-btn" onClick={applyPromoCode} disabled={promoLoading || !promoInput.trim()}>
                    {promoLoading ? '…' : 'Apply'}
                  </button>
                </div>
              )}
              {promoError && <small className="promo-error">{promoError}</small>}
            </div>

            {appliedPromo && discountAmount > 0 && (
              <div className="summary-row promo-discount-row">
                <span>Discount ({appliedPromo.discount_percent}%)</span>
                <span style={{ color: '#0e8f5b' }}>- Rs. {discountAmount.toLocaleString()}</span>
              </div>
            )}

            <div className="summary-total"><span>Total</span><span>Rs. {total.toLocaleString()}</span></div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default CheckoutPage;